import { useTranslation } from "react-i18next"
import { ChangeEvent, useEffect, useState } from "react"
import { useStore } from "../../../contexts/store"
import { Folder, PdfFile } from "../../../shared/src/types/Content"
import { Op_addFolder, Op_editFolder, Op_editPdfFile } from "../../../shared/src/types/Ops"
import { PodClass } from "../../../classes/Pod"
import { validateString } from "../../../shared/src/validation/basicValidationFunctions"

import DialogHeader from "../../Elements/DialogHeader"
import { Box, Button, Dialog, DialogActions, DialogContent, FormControlLabel, FormGroup, Switch, TextField } from "@mui/material"
import dayjs from 'dayjs'


const SettingsDialog = ({item, itemType, handleClose, open, pod}: {item: PdfFile|Folder|null, itemType: string, handleClose: () => void, open: boolean, pod: PodClass}) => {
  const { t } = useTranslation()
  const { opStore, podStore, uiStore, sessionStore, validatorStore } = useStore()
  const [name, setName] = useState<string>("")
  const [nameIsValid, setNameIsValid] = useState(true)
  const [hidden, setHidden] = useState(false)
  const [description, setDescription] = useState<string>("")
  const [descriptionIsValid, setDescriptionIsValid] = useState(true)
  const deviceWidth = uiStore.deviceWidth
  const isFullscreenMode = deviceWidth === "small" || deviceWidth === "smaller" || deviceWidth === "tiny"

  // load data into component
  useEffect(() => {
    if(item) {
      setName(item.name)
      setHidden(item.hidden ? true : false)
      setDescription(item.description ? item.description : "")
    } else {
      setName("")
      setHidden(false)
      setDescription("")
    }
  }, [item, open])

  function handleSave() {
    // add folder
    if(itemType === "folder" && item === null && name && nameIsValid && descriptionIsValid && sessionStore.session) {
      const weight = podStore.getMaxContentWeight() + 1
      const op:Op_addFolder = {
        op: 'addFolder',
        podId: pod.podId,
        data: {
          userId: sessionStore.session.user.userId,
          userName: podStore.userPseudonym || '',
          usergroupId: pod.getUsergroupByRole('Pod').usergroupId,
          description: description,
          folderId: sessionStore.createUuid(),
          hidden: hidden,
          name: name,
          weight: weight,
          coid: null,
          tCreated: dayjs().unix(),
          tModified: dayjs().unix()
        }
      }
      opStore.doOp(op)
    }
    // edit file
    else if(itemType === "file" && name && nameIsValid && descriptionIsValid && sessionStore.session) {
      const file = item as PdfFile
      const op:Op_editPdfFile = {
        op: 'editPdfFile',
        podId: pod.podId,
        data: {
          nodeId: file.nodeId,
          usergroupId: pod.getUsergroupByRole('Pod').usergroupId,
          userId: sessionStore.session.user.userId,
          userName: podStore.userPseudonym || '',
          mods: {
            description: description,
            hidden: hidden,
            name: name
          }
        }
      }
      opStore.doOp(op)
    }
    // edit folder
    else if(itemType === "folder" && name && nameIsValid && descriptionIsValid && sessionStore.session) {
      const folder = item as Folder
      const op:Op_editFolder = {
        op: 'editFolder',
        podId: pod.podId,
        data: {
          folderId: folder.folderId,
          usergroupId: pod.getUsergroupByRole('Pod').usergroupId,
          userId: sessionStore.session.user.userId,
          userName: podStore.userPseudonym || '',
          mods: {
            description: description,
            hidden: hidden,
            name: name
          }
        }
      }
      opStore.doOp(op)
    }
    // do not close, if name was not set or is invalid
    if(name && nameIsValid && descriptionIsValid) {
      handleClose()
    }
  }

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    // check if name is valid
    const testCase = itemType === "file" ? "pdfFile.name" : "folder.name"
    const validName = validatorStore.validate(() => { validateString(event.target.value, testCase, validatorStore.logger) })
    setNameIsValid(validName as boolean)
    // update name
    setName(event.target.value)
  }

  const handleVisibilityChange = (event: ChangeEvent<HTMLInputElement>) => {
    setHidden(event.target.checked)
  }

  const handleDescriptionChange = (event: ChangeEvent<HTMLInputElement>) => {
    // check if description is valid
    const testCase = itemType === "file" ? "pdfFile.description" : "folder.description"
    const validDescription = validatorStore.validate(() => { validateString(event.target.value, testCase, validatorStore.logger) })
    setDescriptionIsValid(validDescription as boolean)
    // update description
    setDescription(event.target.value)
  }

  return(
    <Dialog
      disableRestoreFocus
      fullScreen={isFullscreenMode ? true : false}
      fullWidth
      maxWidth="md"
      onClose={handleClose}
      open={open}
      slotProps={{
        paper: {
          sx: {
            borderRadius: isFullscreenMode ? "" : "16px"
          }
        }
      }}
    >
      <DialogHeader
        backgroundColor="primary.main"
        handleClose={handleClose}
        iconId={itemType === "file" ? "podFile" : "podFolder"}
        label={itemType === "file" ? t('Edit file') : (item === null ? t('Add folder') : t('Edit folder'))}
      />
      <DialogContent sx={{margin: "10px 0"}}>
        <Box  sx={{
          display: "grid",
          gap: "10px",
          maxWidth: "700px",
          justifySelf: "center",
          width: "100%"
        }}>
          <TextField
            autoFocus
            error={(!name || !nameIsValid) ? true : false}
            helperText={!name ? t('The field must not be empty') : !nameIsValid ? t("The input entered is too long or contains invalid characters") : ""}
            fullWidth
            onChange={handleNameChange}
            label={t('Name')}
            required
            value={name}
            variant="outlined"
            onKeyDown={(ev) => {
              if(ev.key === 'Enter') {
                handleSave()
                ev.preventDefault()
              }
            }}
          />
          <FormGroup sx={{marginBottom: "5px", padding: "0 5px"}}>
            <FormControlLabel label={t('Hide from regular pod users')} control={
            <Switch checked={hidden} onChange={handleVisibilityChange} />
          } />
          </FormGroup>
          <TextField
            error={(!descriptionIsValid) ? true : false}
            helperText={!descriptionIsValid ? t("The input entered is too long or contains invalid characters") : ""}
            minRows={3}
            maxRows={3}
            fullWidth
            multiline
            onChange={handleDescriptionChange}
            label={t('Description')+t(' (optional) ')}
            value={description}
            variant="outlined"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{
        display: "flex",
        gap: "10px",
        justifyItems: "end",
        padding: "20px 25px"
      }}>
        <Button onClick={handleClose}>
          {t('Cancel')}
        </Button>
        <Button variant="contained" onClick={handleSave}>
          {t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SettingsDialog